import type { UserRole } from "../auth/role-policy";
import type { AccessClass } from "../baseline/route-equivalence";

export type RouteLayout = "modern" | "blank";
export type RouteContainer = "default" | "gutterless" | "contained";
export type RouteMode = "list" | "new";

export type RouteMetadata = {
  routeKey: string;
  access: AccessClass;
  layout: RouteLayout;
  container: RouteContainer;
  footer?: boolean;
  requiredRoles: readonly UserRole[];
  requiredCompanyRole?: "Owner";
  leafKind?: "preview";
  mode?: RouteMode;
};

export type RouteMatchProjection = {
  routeId?: string;
  staticData?: unknown;
};

export type ActiveRouteProjection = RouteMetadata & {
  routeId?: string;
};

const isRouteMetadata = (value: unknown): value is RouteMetadata =>
  typeof value === "object" &&
  value !== null &&
  "routeKey" in value &&
  typeof value.routeKey === "string";

export function selectActiveRoute(
  matches: readonly RouteMatchProjection[],
): ActiveRouteProjection | null {
  const leaf = [...matches]
    .reverse()
    .find((match) => isRouteMetadata(match.staticData));

  return leaf && isRouteMetadata(leaf.staticData)
    ? { ...leaf.staticData, routeId: leaf.routeId }
    : null;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption extends Partial<RouteMetadata> {}
}
